import React from 'react';
import { StyleSheet } from 'react-native';
import { Form, HStack, LoadingPlaceholder, theme } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { US_PHONE_NUMBER_MAX_LENGTH } from './constants';
import type { CustomerProfileFieldsQuery } from './GraphQL/Profile.generated';
import type { useUpdateCustomerProfileMutation } from './GraphQL/Profile.generated';
import type { ProfileFormData } from './useProfileForm';
import { useProfileForm } from './useProfileForm';

// ────────────────────────────────────────────────────────────────────────────────

export const ProfileForm = (props: ProfileFormProps) => {
  const { customer, isLoading, onSaveCustomer, isSubmitting } = props;
  const { t } = useLocalizationContext();

  // ────────────────────────────────────────────────────────────────────

  const { form, isSubmitAllowed, resetForm } = useProfileForm({ customer });

  const { control, handleSubmit, formState, resetField } = form;
  const { errors, isSubmitSuccessful, dirtyFields } = formState;
  const { track } = useTelemetry();

  // ─── HELPERS ────────────────────────────────────────────────────────

  const onSubmit = React.useCallback(
    async (formData: ProfileFormData) => {
      const { firstName, lastName, phoneNumber, confirmPassword } = formData;
      const changedFields = Object.keys(dirtyFields).filter(
        (key) => key !== 'confirmPassword',
      );

      track('account.profile_saved', { changedFields });

      await onSaveCustomer({
        input: {
          firstName,
          lastName,
          contactNumber: phoneNumber,
          password: confirmPassword,
        },
      });
    },
    [dirtyFields, onSaveCustomer, track],
  );

  // ─── FORM EFFECTS ────────────────────────────────────────────────────────

  useTrackEffect('account.profile_viewed');

  React.useEffect(() => {
    resetForm();
  }, [customer, resetForm]);

  React.useEffect(() => {
    if (isSubmitSuccessful && !isSubmitting) {
      resetField('confirmPassword');
      resetField('password');
    }
  }, [isSubmitSuccessful, isSubmitting, resetField]);

  // ────────────────────────────────────────────────────────────────────────────────

  if (isLoading) return <AccountScreenLoading />;

  return (
    <Form.Container contentStyle={styles.containerContent} removeSpacing={true}>
      {/* ========= = first name ========== */}
      <Form.Group>
        <Form.TextField
          name="firstName"
          control={control}
          rules={{ required: true }}
          testID="account.profile.firstName"
          label={t('form.first-name')}
          hideSubmitButton
          autoComplete="name"
          notice={errors.firstName?.message}
        />
      </Form.Group>

      {/* ========= = last name ========== */}
      <Form.Group>
        <Form.TextField
          name="lastName"
          control={control}
          rules={{ required: true }}
          testID="account.profile.lastName"
          label={t('form.last-name')}
          hideSubmitButton
          autoComplete="name"
          notice={errors.lastName?.message}
        />
      </Form.Group>

      {/* ========= = email ========== */}
      <Form.Group>
        <Form.TextField
          name="email"
          control={control}
          rules={{ required: true }}
          keyboardType="email-address"
          testID="account.profile.email"
          label={t('form.email')}
          hideSubmitButton
          autoComplete="email"
          notice={errors.email?.message}
          disabled={true}
          accessibilityState={{ disabled: true }}
        />
      </Form.Group>

      {/* ========= = phone number ========== */}
      <Form.Group>
        <Form.TextFieldPhoneNumber
          name="phoneNumber"
          control={control}
          rules={{ required: true }}
          testID="account.profile.phoneNumber"
          label={t('form.phone-number')}
          maxLength={US_PHONE_NUMBER_MAX_LENGTH}
          hideSubmitButton
          notice={errors.phoneNumber?.message}
        />
      </Form.Group>

      {/* ========= = password ========== */}
      <Form.Group>
        <Form.TextFieldPassword
          name="password"
          control={control}
          rules={{ required: true }}
          testID="account.profile.password"
          label={t('form.password')}
          hideSubmitButton
          autoComplete="password-new"
          notice={errors.password?.message}
          toggleTestID="field-password-toggle"
        />
      </Form.Group>

      {/* ========= = confirm password ========== */}
      <Form.Group>
        <Form.TextFieldPassword
          name="confirmPassword"
          control={control}
          rules={{ required: true }}
          testID="account.profile.confirmPassword"
          label={t('form.confirm-password')}
          hideSubmitButton
          autoComplete="password-new"
          notice={errors.confirmPassword?.message}
          toggleTestID="field-confirm-password-toggle"
        />
      </Form.Group>

      {/* ========= = ========== ========== */}
      <HStack itemsPerRow={2} style={styles.buttonContainer}>
        <Form.SubmitButton
          palette="secondary"
          onPress={resetForm}
          testID="account.profile.cancel-button"
          style={styles.button}
        >
          {t('account.profile.cancel')}
        </Form.SubmitButton>

        <Form.SubmitButton
          testID="account.profile.save-button"
          disabled={!isSubmitAllowed}
          isLoading={isSubmitting}
          onPress={handleSubmit(onSubmit)}
          accessibilityState={{ disabled: !isSubmitAllowed }}
          style={styles.button}
        >
          {t('account.profile.save')}
        </Form.SubmitButton>
      </HStack>
    </Form.Container>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerContent: { paddingVertical: 0 },
  buttonContainer: { paddingTop: theme.spacing.medium },
  button: { paddingTop: 0 },
});

// ────────────────────────────────────────────────────────────────────────────────

type ProfileFormProps = {
  readonly customer: CustomerProfileFieldsQuery['currentCustomer'];
  readonly isLoading: boolean;
  readonly isSubmitting: boolean;
  readonly onSaveCustomer: UpdateCallback;
};

type UpdateCallback = ReturnType<typeof useUpdateCustomerProfileMutation>[1];

// ────────────────────────────────────────────────────────────────────────────────

const AccountScreenLoading = () => (
  <Form.Container>
    <LoadingPlaceholder
      rows={8}
      rowHeight={[100, 60, 70, 70, 70, 70, 70, 80]}
      palette="cream"
    />
  </Form.Container>
);
